import React from 'react';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import subscriptionUtils from './subscriptionUtils';
import {multipleSubscriptionsUtils, solutionUtils} from './solutionUtils';
/*API Format
{"data_sets": [
      {
        "data_type": "NEW_PATIENTS",
        "total": 0,
        "average": 0,
        "data_set": [
          {
              "data_time": "2024-02-19",
              "data_value": 999
          },
          {
              "data_time": "2024-02-26",
              "data_value": 555
          },
        ]
      },
      {
        "data_type": "RETURNING_PATIENTS",
        "total": 0,
        "average": 0,
        "data_set": []
      }
 ]}
*/
const getI18n = (name, options) => {
  const correspondingOption = options.find(option => option.value === name);
  if (correspondingOption && correspondingOption.label)
    return correspondingOption.label;
  return name;
};

const allObjsEmpty = objs => {
  for (let key in objs) {
    if (objs.hasOwnProperty(key) && !_isEmpty(objs[key])) {
      return false;
    }
  }
  return true;
};

const getSortBy = (type = 'numAsc', data = []) => {
  if (!data || data.length === 0) return [];
  if (type === 'numAsc') {
    data.sort((a, b) => a[1] - b[1]);
  }
  if (type === 'numDesc') {
    data.sort((a, b) => b[1] - a[1]);
  }
  if (type === 'alphaAsc') {
    data.sort((a, b) => {
      if (a[0] > b[0]) {
        return -1;
      }
      if (a[0] < b[0]) {
        return 1;
      }
      return 0;
    });
  }
  if (type === 'alphaDesc') {
    data.sort((a, b) => {
      if (a[0] < b[0]) {
        return -1;
      }
      if (a[0] > b[0]) {
        return 1;
      }
      return 0;
    });
  }
  return data;
};

const utils = {
  formattedDataSet: (rawDataSet = {}) => {
    if (_isEmpty(rawDataSet) || _get(rawDataSet, 'data_set', []).length === 0)
      return {};
    return {
      sinceTime: rawDataSet.data_set[0].data_time || '2023-07-03',
      untilTime:
        rawDataSet.data_set[rawDataSet.data_set.length - 1].data_time ||
        '2023-07-24',
      total: rawDataSet.total || 0,
      avg: rawDataSet.average || 0,
      data: rawDataSet.data_set.map(data => ({
        time: data.data_time,
        count: data.data_value,
      })),
    };
  },
  formattedDataSets: (dataSets = {}) => {
    if (_isEmpty(dataSets) || !_get(dataSets, 'data_sets')) return {};
    const formattedData = {};
    for (const dataSet of dataSets.data_sets) {
      formattedData[dataSet.data_type] = utils.formattedDataSet(dataSet);
    }
    return formattedData;
  },
  multipleLineChartFormat: (dataSets = {}, options) => {
    if (_isEmpty(dataSets) || allObjsEmpty(dataSets) || !options) return;
    return {
      ...dataSets,
      valid: Object.entries(dataSets).map(([key, dataSet]) => ({
        name: getI18n(key, options),
        data: _get(dataSet, 'data', []).map(dataItem => ({
          count: dataItem.count,
          date: dataItem.time,
        })),
      })),
    };
  },
  pieChartFormat: (dataSet = {}, options) => {
    if (_isEmpty(dataSet) || !options) return;
    return {
      ...dataSet,
      valid: dataSet.map(item => ({
        name: getI18n(item.group, options),
        y: item.ratio * 100,
      })),
    };
  },
  histogramFormat: (
    dataSet = {},
    options,
    sortBy = 'numAsc',
    scoreFormat = 'percentile'
  ) => {
    if (_isEmpty(dataSet) || !options) return;

    const datasetMap = dataSet.reduce((map, item) => {
      map[item.data_type] =
        scoreFormat === 'percentile' ? item.average : item.average / 10;
      return map;
    }, {});

    const result = options.map(option => {
      return [option.label, datasetMap[option.value] || 0];
    });

    return {
      ...dataSet,
      valid: getSortBy(sortBy, result),
    };
  },
  coverFormat: data => {
    return {
      data:
        _get(data, 'data') && _get(data, 'data').length > 0
          ? _get(data, 'data').map(s => ({
              count: s.count,
              date: s.time || s.date,
            }))
          : [],
    };
  },
  overViewCoverFormat: data => {
    if (!data) return {};
    return {
      ...data,
      overall: {
        avgNewCount: data.avgNewCount || data.avg || 0,
        sinceTime: data.sinceTime || '2023-02-14',
        totalCount: data.totalCount || data.total || 0,
        untilTime: data.untilTime || '2023-03-14',
      },
      valid: _get(data, 'data'),
      data: utils.coverFormat(data).data,
    };
  },
  showSkinAnalysisAnalytics: auth => {
    const isOwner = subscriptionUtils.isOwnerRole(auth);
    const isSkinAnalysis = solutionUtils.isSkinAnalysis(auth);
    const overdueSolutions = multipleSubscriptionsUtils.overdueSolutions(auth);
    const isSkinAnalysisOverdue = overdueSolutions.includes('SKIN_ANALYSIS');
    return isSkinAnalysis || (isOwner && isSkinAnalysisOverdue);
  },
  showSimulationAnalytics: auth => {
    const isOwner = subscriptionUtils.isOwnerRole(auth);
    const isSimulation = solutionUtils.isSimulation(auth);
    const overdueSolutions = multipleSubscriptionsUtils.overdueSolutions(auth);
    const isSimulationOverdue = overdueSolutions.includes(
      'AESTHETIC_SIMULATION'
    );
    return isSimulation || (isOwner && isSimulationOverdue);
  },
  convertGranularityForOverview: granularity => {
    const mapping = {
      W: 'week',
      D: 'day',
      M: 'month',
      Q: 'quarter',
    };
    return mapping[granularity] || 'week';
  },
};

export default utils;
